/**
 * Child App Main Application Logic (Redesigned)
 * Handles routing and app initialization with new 3-icon navigation
 */

import { router } from './utils/router.js';
import { t } from './i18n/config.js';
import { NavigationBar } from './components/NavigationBar.js';
import { NavigationOverlay } from './components/NavigationOverlay.js';
import { ProfileScreen } from './views/ProfileScreen.js';
import { LibraryScreen } from './views/LibraryScreen.js';
import { AchievementBoard } from './views/AchievementBoard.js';
import { renderDeviceActivation } from './views/DeviceActivation.js';
import { renderStoryReader, cleanupStoryReader } from './views/StoryReader.js';
import { renderGamePlayer } from './views/GamePlayer.js';
import { renderWordSearchPlayer } from './views/WordSearchPlayer.js';

// Global instances
let navigationBar = null;
let navigationOverlay = null;
let currentView = null;
// initializeMainAppStructure runs again on the adult deep-link path, so the
// language listener is bound once rather than stacking duplicates
let languageChangeListenerBound = false;

/**
 * Initialize the child application
 */
export async function initializeChildApp() {
    try {
        console.log('🚀 Initializing Child App (Redesigned)...');

        // Check for preview mode (must be before other checks)
        const urlParams = new URLSearchParams(window.location.search);
        const previewToken = urlParams.get('token');

        if (previewToken) {
            console.log('👁️ Preview mode detected - initializing with preview content');
            // Initialize main app structure first, then let preview handler take over
            await initializeMainApp(null); // No profile needed for preview
            return;
        }

        // Check for direct module access (preview mode)
        const path = window.location.pathname;
        const moduleMatch = path.match(/^\/module\/([a-f0-9-]+)$/i);

        if (moduleMatch) {
            const moduleId = moduleMatch[1];
            console.log('📖 Direct module access detected:', moduleId);

            // Initialize main app structure WITHOUT navigating to profile
            await initializeMainAppStructure(null); // No profile needed for direct access

            // Load module directly without device authorization
            const { loadModuleDirectly } = await import('./services/directModuleLoader.js');
            await loadModuleDirectly(moduleId);
            return;
        }

        // Check for adult recording mode (deep link from admin app)
        // URL: ?mode=record-adult&moduleId=...&familyId=...&displayName=...&userId=...
        const recordMode = urlParams.get('mode');
        if (recordMode === 'record-adult') {
            const moduleId = urlParams.get('moduleId');
            const familyId = urlParams.get('familyId');
            const displayName = urlParams.get('displayName') || 'Family';
            const userId = urlParams.get('userId') || 'adult-recorder';
            const avatarUrl = urlParams.get('avatarUrl') || '';

            if (moduleId && familyId) {
                console.log('🎙️ Adult recording mode detected:', { moduleId, familyId, displayName, userId });

                // Hide navigation immediately — adult shouldn't see child nav
                document.documentElement.dataset.adultRecordMode = 'true';

                // Initialize main app structure
                await initializeMainAppStructure(null);

                // Hide nav bar visually but keep space (prevents book from expanding and clipping page numbers)
                const navBar = document.querySelector('.navigation-bar');
                if (navBar) navBar.style.visibility = 'hidden';
                const navContainer = document.getElementById('navigation-container');
                if (navContainer) navContainer.style.visibility = 'hidden';

                // Launch adult recording mode in story reader
                const { renderAdultRecordingMode } = await import('./views/StoryReader.js');
                await renderAdultRecordingMode({ moduleId, familyId, displayName, userId, avatarUrl });
                return;
            } else {
                console.warn('⚠️ Adult recording mode missing moduleId or familyId');
            }
        }

        // Check for debug mode (only for debug overlay, not test data)
        // To use test data, use ?testdata=true instead
        const debugMode = urlParams.get('debug') === 'true';
        const testDataMode = urlParams.get('testdata') === 'true';

        if (testDataMode) {
            console.log('🧪 Test data mode enabled - setting up test data');
            const { setupTestData } = await import('./services/testData.js');
            setupTestData();
        } else {
            // Clear any existing test data if not in test mode
            const authorizedFamily = localStorage.getItem('authorizedFamily');
            if (authorizedFamily) {
                try {
                    const family = JSON.parse(authorizedFamily);
                    if (family.id === 'test-family-123') {
                        console.log('🧹 Clearing old test data');
                        const { clearTestData } = await import('./services/testData.js');
                        clearTestData();
                    }
                } catch (e) {
                    // Ignore parse errors
                }
            }
        }

        // Check for auth token (one-click device setup from admin app)
        const authToken = urlParams.get('auth');
        if (authToken) {
            console.log('🔑 Auth token detected, attempting auto-registration...');
            try {
                const { api } = await import('./services/api.js');
                const result = await api.post('/api/auth/device/redeem-token', {
                    token: authToken
                });

                if (result.authorized) {
                    console.log('✅ Device auto-registered via token:', result);

                    // Store device credentials (same format as code-based activation)
                    const deviceAuth = { deviceId: result.deviceId, authorized: true };
                    localStorage.setItem('deviceAuthorization', JSON.stringify(deviceAuth));
                    localStorage.setItem('authorizedFamily', JSON.stringify({
                        id: result.familyId,
                        name: result.familyName
                    }));
                    localStorage.setItem('kassie_device_id', result.deviceId);

                    // Park the handoff token so Add to Home Screen captures it
                    const { parkHandoffToken } = await import('./utils/handoff.js');
                    parkHandoffToken(result.handoffToken);

                    // Show PWA install prompt after successful registration
                    const { showPWAInstallPrompt } = await import('./utils/pwaInstall.js');
                    showPWAInstallPrompt();

                    // Continue to profile selection (reload to trigger normal flow)
                    window.location.reload();
                    return;
                }
            } catch (error) {
                console.error('❌ Token redemption failed:', error);

                // Show friendly error message
                const app = document.getElementById('app');
                if (app) {
                    app.innerHTML = `
                        <div class="token-error-container">
                            <img src="/svg/branding/KassieAvatar.svg" alt="Kassie" width="120" />
                            <h2>${t('child.device.tokenExpired') || 'Hierdie skakel het verval'}</h2>
                            <p>${t('child.device.tokenExpiredHelp') || 'Vra jou ouer om \'n nuwe skakel te stuur vanuit die Kassie ouer-app.'}</p>
                            <button class="custom-button" onclick="window.location.href='/'">
                                <div class="bottom-div cyanBG"></div>
                                <div class="top-div kassieBG">
                                    <span class="button-text">${t('child.device.tryAgain') || 'Probeer Weer'}</span>
                                </div>
                            </button>
                        </div>
                    `;
                }
                return;
            }
        }

        // Check if device is authorized
        const deviceAuth = localStorage.getItem('deviceAuthorization');
        const authorizedFamily = localStorage.getItem('authorizedFamily');
        const selectedProfile = localStorage.getItem('selectedProfile');

        // Initialize diagnostics early so we can log boot steps
        const { recDbg } = await import('./utils/recordingDebug.js');
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

        // recDbg.log('=== BOOT START ===', {
        //     standalone: isStandalone,
        //     hasDeviceAuth: !!deviceAuth,
        //     hasFamily: !!authorizedFamily,
        //     hasProfile: !!selectedProfile,
        //     url: window.location.href.substring(0, 80),
        //     search: window.location.search.substring(0, 50),
        //     online: navigator.onLine,
        // });
        recDbg.log('Device fingerprint', {
            userAgent: navigator.userAgent,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            uaFirst100: navigator.userAgent.substring(0, 100),
        });

        // Handoff exchange — a freshly installed PWA starts with an empty
        // storage partition, so recover the device identity the browser
        // session already holds. Only runs when there is nothing stored AND
        // a handoff token is present in the URL.
        if (!deviceAuth) {
            recDbg.log('No deviceAuth in localStorage — attempting recovery');

            // 1. CacheStorage recovery (shared between Safari and PWA on iOS 14+)
            try {
                if ('caches' in window) {
                    const cache = await caches.open('kassie-device-auth');
                    const response = await cache.match('/device-auth-state');
                    if (response) {
                        const data = await response.json();
                        if (data && data.authorized && data.deviceId && data.familyId) {
                            recDbg.ok('CacheStorage recovery SUCCESS', { deviceId: data.deviceId, familyId: data.familyId });
                            localStorage.setItem('deviceAuthorization', JSON.stringify({
                                deviceId: data.deviceId, authorized: true
                            }));
                            localStorage.setItem('authorizedFamily', JSON.stringify({
                                id: data.familyId, name: data.familyName || ''
                            }));
                            localStorage.setItem('kassie_device_id', data.deviceId);
                            window.location.reload();
                            return;
                        }
                    } else {
                        recDbg.log('CacheStorage: no auth data found');
                    }
                }
            } catch (cacheErr) {
                recDbg.warn('CacheStorage recovery failed', { message: cacheErr?.message });
            }

            // 2. URL handoff token
            const { takeHandoffToken } = await import('./utils/handoff.js');
            const handoffToken = takeHandoffToken();

            if (handoffToken) {
                recDbg.log(`Handoff token found in URL (${handoffToken.length} chars)`, { standalone: isStandalone, tokenPreview: handoffToken.substring(0, 8) + '...' });

                // Check token validity before exchange
                try {
                    const { api } = await import('./services/api.js');
                    const validity = await api.post('/api/auth/device/handoff/check', { handoffToken });
                    recDbg.log(`Token check: ${validity.valid ? '✅ VALID' : '❌ INVALID'}`, {
                        consumed: validity.consumed,
                        expired: validity.expired,
                        expiresAt: validity.expiresAt,
                    });
                } catch (checkErr) {
                    recDbg.warn('Token check failed', { message: checkErr?.message });
                }

                // Attempt exchange
                try {
                    const { api } = await import('./services/api.js');
                    recDbg.log('Calling handoff/exchange...', { standalone: isStandalone });
                    const result = await api.post('/api/auth/device/handoff/exchange', {
                        handoffToken,
                        standalone: isStandalone,
                    });

                    recDbg.log('Exchange response received', { authorized: result?.authorized, deviceId: result?.deviceId });

                    if (result && result.authorized) {
                        recDbg.ok('Handoff exchange SUCCESS', { deviceId: result.deviceId, familyId: result.familyId });
                        localStorage.setItem('deviceAuthorization', JSON.stringify({
                            deviceId: result.deviceId, authorized: true
                        }));
                        localStorage.setItem('authorizedFamily', JSON.stringify({
                            id: result.familyId, name: result.familyName
                        }));
                        localStorage.setItem('kassie_device_id', result.deviceId);

                        history.replaceState(null, '', '/');
                        recDbg.ok('Reloading after successful exchange...');
                        window.location.reload();
                        return;
                    } else {
                        recDbg.warn('Exchange returned NOT authorized', result);
                    }
                } catch (error) {
                    recDbg.error('Handoff exchange FAILED', { message: error?.message, status: error?.status });
                }

                // Clear the dead parameter so a reload does not retry it
                history.replaceState(null, '', '/');
            } else {
                recDbg.log('No handoff token in URL — trying fingerprint lookup');

                // Try JS cookie first (set on app.kassie.media during activation)
                try {
                    const cookieMatch = document.cookie.match(/kassie_device_js=([^;]+)/);
                    if (cookieMatch) {
                        const cookieData = JSON.parse(decodeURIComponent(cookieMatch[1]));
                        recDbg.ok('JS cookie found!', { deviceId: cookieData.deviceId, familyId: cookieData.familyId });
                        localStorage.setItem('deviceAuthorization', JSON.stringify({
                            deviceId: cookieData.deviceId, authorized: true
                        }));
                        localStorage.setItem('authorizedFamily', JSON.stringify({
                            id: cookieData.familyId, name: cookieData.familyName
                        }));
                        localStorage.setItem('kassie_device_id', cookieData.deviceId);
                        recDbg.ok('Recovered from JS cookie — reloading');
                        window.location.reload();
                        return;
                    } else {
                        recDbg.log('No JS cookie found');
                    }
                } catch (cookieErr) {
                    recDbg.warn('JS cookie read failed', { message: cookieErr?.message });
                }

                // PWA launched without ?hoff= (iOS stripped it) — fetch token by device fingerprint
                if (navigator.onLine && isStandalone) {
                    recDbg.log('Standalone + online → calling handoff/latest with fingerprint');
                    try {
                        const { api } = await import('./services/api.js');
                        const lookupResult = await api.post('/api/auth/device/handoff/latest', {
                            userAgent: navigator.userAgent,
                            screenWidth: window.screen.width,
                            screenHeight: window.screen.height,
                        });

                        if (lookupResult && lookupResult.handoffToken) {
                            recDbg.ok('Fingerprint lookup found token', { tokenPreview: lookupResult.handoffToken.substring(0, 8) + '...' });

                            const exchangeResult = await api.post('/api/auth/device/handoff/exchange', {
                                handoffToken: lookupResult.handoffToken,
                                standalone: true,
                            });

                            if (exchangeResult && exchangeResult.authorized) {
                                recDbg.ok('Fingerprint exchange SUCCESS', { deviceId: exchangeResult.deviceId });
                                localStorage.setItem('deviceAuthorization', JSON.stringify({
                                    deviceId: exchangeResult.deviceId, authorized: true
                                }));
                                localStorage.setItem('authorizedFamily', JSON.stringify({
                                    id: exchangeResult.familyId, name: exchangeResult.familyName
                                }));
                                localStorage.setItem('kassie_device_id', exchangeResult.deviceId);
                                recDbg.ok('Reloading after fingerprint recovery...');
                                window.location.reload();
                                return;
                            } else {
                                recDbg.warn('Fingerprint exchange NOT authorized', exchangeResult);
                            }
                        } else {
                            recDbg.warn('Fingerprint lookup: no token returned', lookupResult);
                        }
                    } catch (fpErr) {
                        recDbg.error('Fingerprint lookup failed', { message: fpErr?.message, status: fpErr?.status });
                    }
                } else {
                    recDbg.log('Skipping fingerprint lookup', { online: navigator.onLine, standalone: isStandalone });
                }
            }

            // Cookie-based recovery as last resort
            if (navigator.onLine) {
                recDbg.log('Attempting cookie-based recovery...');
                try {
                    const { api } = await import('./services/api.js');
                    const result = await api.post('/api/auth/device/cookie-recover', {});

                    if (result && result.authorized) {
                        recDbg.ok('Cookie recovery SUCCESS', { deviceId: result.deviceId });
                        localStorage.setItem('deviceAuthorization', JSON.stringify({
                            deviceId: result.deviceId, authorized: true
                        }));
                        localStorage.setItem('authorizedFamily', JSON.stringify({
                            id: result.familyId, name: result.familyName
                        }));
                        localStorage.setItem('kassie_device_id', result.deviceId);
                        window.location.reload();
                        return;
                    }
                } catch (error) {
                    recDbg.warn('Cookie recovery failed', { message: error?.message });
                }
            } else {
                recDbg.warn('Offline — skipping cookie recovery');
            }

            recDbg.error('ALL recovery methods failed — showing activation screen');
        } else {
            recDbg.log('Device authorized (localStorage present)', { deviceId: JSON.parse(deviceAuth).deviceId });
        }

        if (!deviceAuth || !authorizedFamily) {
            // Show device activation screen
            console.log('📱 Device not authorized, showing activation screen');
            await renderDeviceActivation();
            return;
        }

        // Verify device authorization with server if online
        if (navigator.onLine) {
            console.log('🌐 Device is online, verifying authorization with server...');
            try {
                const auth = JSON.parse(deviceAuth);
                const family = JSON.parse(authorizedFamily);

                const { api } = await import('./services/api.js');
                const response = await api.get('/api/auth/device/check', {
                    device_id: auth.deviceId,
                    family_id: family.id
                });

                if (!response.authorized) {
                    console.warn('⚠️ Device authorization revoked by server');
                    // Clear local storage
                    localStorage.removeItem('deviceAuthorization');
                    localStorage.removeItem('authorizedFamily');
                    localStorage.removeItem('selectedProfile');

                    // Show activation screen with revocation message
                    await renderDeviceActivation({ revoked: true });
                    return;
                }

                // Sync family name and device name from server (keeps localStorage fresh)
                if (response.familyName || response.deviceName) {
                    const updatedFamily = {
                        ...family,
                        name: response.familyName || family.name,
                        deviceName: response.deviceName || family.deviceName || '',
                    };
                    localStorage.setItem('authorizedFamily', JSON.stringify(updatedFamily));
                }

                console.log('✅ Device authorization verified with server');
            } catch (error) {
                console.error('❌ Failed to verify device authorization:', error);
                // If verification fails due to network error, allow offline access
                // but log the error for debugging
                console.warn('⚠️ Continuing with cached authorization due to network error');
            }
        } else {
            console.log('📴 Device is offline, using cached authorization');
        }

        // Initialize main app with navigation (profile can be null initially)
        const profile = selectedProfile ? JSON.parse(selectedProfile) : null;
        console.log('👤 Profile data:', profile);

        // Check for adult recording mode (launched from admin app)
        const adultRecordingMode = urlParams.get('mode') === 'adult-recording';
        const adultModuleId = urlParams.get('moduleId');
        const adultFamilyId = urlParams.get('familyId');
        const adultDisplayName = urlParams.get('displayName') || 'Adult';

        if (adultRecordingMode && adultModuleId) {
            console.log('🎙️ Adult recording mode - launching story reader directly', {
                moduleId: adultModuleId,
                familyId: adultFamilyId,
                displayName: adultDisplayName
            });

            // Initialize app structure without navigating to profile
            await initializeMainAppStructure(profile);

            // Load the module data from the content catalog
            try {
                const { api } = await import('./services/api.js');
                const family = JSON.parse(authorizedFamily);
                const catalogResponse = await api.get('/api/content/catalog', {
                    family_id: family.id
                });
                const modules = catalogResponse?.modules || [];
                const moduleData = modules.find(m => m.id === adultModuleId);

                if (!moduleData) {
                    console.error('❌ Module not found for adult recording:', adultModuleId);
                    await router.navigate('profile', profile);
                    return;
                }

                // Use existing profile or create a synthetic adult profile
                const adultProfile = profile || {
                    id: 'adult-recorder',
                    name: adultDisplayName,
                    isAdult: true
                };
                // Ensure familyId is set (needed for recording uploads)
                if (!adultProfile.familyId && !adultProfile.family_id) {
                    adultProfile.familyId = adultFamilyId || family.id;
                }

                // Navigate directly to story reader
                await router.navigate('story-reader', adultProfile, moduleData);
            } catch (error) {
                console.error('❌ Failed to launch adult recording mode:', error);
                await router.navigate('profile', profile);
            }
            return;
        }

        await initializeMainApp(profile);

        // Start heartbeat ping (every 60s) if profile is selected
        if (profile && profile.id) {
            startHeartbeat(profile.id);
        }

    } catch (error) {
        console.error('❌ Failed to initialize child app:', error);
        throw error;
    }
}

/**
 * Initialize main app structure only (no navigation)
 * Used for direct module access and preview mode
 */
async function initializeMainAppStructure(profile) {
    const app = document.getElementById('app');

    // Clear app container
    app.innerHTML = '';

    // Set up flexbox layout for the entire app
    app.style.display = 'flex';
    app.style.flexDirection = 'column';
    app.style.height = '100vh';
    app.style.overflow = 'hidden';

    // Create navigation container (percentage-based height)
    const navContainer = document.createElement('div');
    navContainer.id = 'navigation-container';
    navContainer.style.flex = '0 0 auto'; // Don't grow or shrink
    navContainer.style.height = ''; // Height set by CSS based on device category
    app.appendChild(navContainer);

    // Create content container (fills remaining space)
    const contentContainer = document.createElement('div');
    contentContainer.id = 'content-container';
    contentContainer.style.flex = '1 1 auto'; // Grow to fill available space
    contentContainer.style.overflow = 'visible'; // Allow card elements to extend beyond boundaries
    contentContainer.style.position = 'relative'; // Positioning context for children
    contentContainer.style.display = 'flex'; // Flexbox for centering constrained content
    contentContainer.style.alignItems = 'center'; // Center vertically
    contentContainer.style.justifyContent = 'center'; // Center horizontally
    app.appendChild(contentContainer);

    // Initialize NavigationBar
    navigationBar = new NavigationBar(navContainer, router, profile);
    navigationBar.render();

    // Initialize NavigationOverlay (for full-screen modes)
    navigationOverlay = new NavigationOverlay(router, profile);
    navigationOverlay.renderOverlay(app);
    navigationOverlay.renderHamburgerButton(app);
    navigationOverlay.hideHamburger(); // Hidden by default

    // Set up overlay callbacks for pause/resume
    setupOverlayCallbacks(navigationOverlay);

    // Register routes
    registerRoutes(contentContainer, profile);

    // Re-translate the page under the settings modal when the language changes
    setupLanguageChangeReload();

    // DON'T navigate to profile - let the caller handle navigation

    console.log('✅ Main app structure initialized (no navigation)');
}

/**
 * Re-translate the current page when the language changes.
 *
 * The settings modal dispatches this after switching language. Without it the
 * page underneath keeps the old language until the user navigates somewhere
 * else, which reads as the setting not having applied.
 *
 * Two strategies, because one size does not fit:
 *
 *   Content pages are rebuilt outright — simplest, and they hold no live state.
 *
 *   Pages with something running are updated in place instead. Rebuilding the
 *   music player would destroy its <audio> element and stop the song; the reader
 *   would lose its place in the book, possibly mid-recording. Those views expose
 *   a retranslate function that swaps the copy and leaves the rest alone.
 *
 * @returns {void}
 */
function setupLanguageChangeReload() {
    if (languageChangeListenerBound) return;
    languageChangeListenerBound = true;

    const REBUILDABLE_ROUTES = ['profile', 'library', 'achievements'];

    /** Routes that must not be rebuilt, and how to re-translate them instead. */
    const IN_PLACE_ROUTES = {
        'music-player': async () => {
            const { retranslateMusicPlayer } = await import('./views/MusicPlayer.js');
            retranslateMusicPlayer();
        },
    };

    window.addEventListener('kassie-language-changed', async () => {
        const route = router.getCurrentRoute();

        try {
            if (REBUILDABLE_ROUTES.includes(route)) {
                await router.reload();
                console.log(`🌍 Language changed — rebuilt "${route}"`);
                return;
            }

            if (IN_PLACE_ROUTES[route]) {
                await IN_PLACE_ROUTES[route]();
                console.log(`🌍 Language changed — re-translated "${route}" in place`);
                return;
            }

            console.log(`🌍 Language changed — no handler for "${route}", left as is`);
        } catch (err) {
            // A failure here must not take the settings modal down with it
            console.warn('⚠️ Could not apply language change to the page:', err.message);
        }
    });
}

/**
 * Initialize main app with navigation
 */
async function initializeMainApp(profile) {
    await initializeMainAppStructure(profile);

    // Navigate to default route (profile selection)
    await router.navigate('profile', profile);

    // Start background sync if a profile is already selected
    if (profile?.id) {
        import('./services/backgroundSync.js').then(({ startBackgroundSync }) => {
            startBackgroundSync(profile.id);
        });
    }

    console.log('✅ Main app initialized with navigation');
}

/**
 * Set up overlay callbacks for pause/resume
 */
function setupOverlayCallbacks(overlay) {
    // Store original open/close methods
    const originalOpen = overlay.open.bind(overlay);
    const originalClose = overlay.close.bind(overlay);

    // Override open method to pause reading
    overlay.open = function () {
        // Check if we're in story reader
        const currentRoute = router.getCurrentRoute();
        if (currentRoute === 'story-reader') {
            // Dynamically import and call pause function
            import('./views/StoryReader.js').then(module => {
                if (module.pauseReadingSession) {
                    module.pauseReadingSession();
                }
            });
        }
        originalOpen();
    };

    // Override close method to resume reading
    overlay.close = function () {
        // Check if we're in story reader
        const currentRoute = router.getCurrentRoute();
        if (currentRoute === 'story-reader') {
            // Dynamically import and call resume function
            import('./views/StoryReader.js').then(module => {
                if (module.resumeReadingSession) {
                    module.resumeReadingSession();
                }
            });
        }
        originalClose();
    };
}

/**
 * Register all routes
 */
function registerRoutes(contentContainer, _profile) {
    // Profile Screen
    router.register('profile', async (profileData) => {
        console.log('📍 Route: Profile');

        // Show navigation bar
        if (navigationBar) {
            navigationBar.exitFilterMode(); // Exit filter mode when leaving library
            navigationBar.show();
            navigationBar.setActive('profile');
            navigationBar.disableReaderMode(); // Disable orange background
        }
        if (navigationOverlay) {
            navigationOverlay.hideHamburger();
        }

        // Destroy previous view
        if (currentView && currentView.destroy) {
            currentView.destroy();
        }

        // Create and render ProfileScreen
        currentView = new ProfileScreen(contentContainer, router, profileData, navigationBar);
        await currentView.render();

        return currentView;
    });

    // Library Screen
    router.register('library', async (profileData) => {
        console.log('📍 Route: Library');

        // Show navigation bar with filter mode
        if (navigationBar) {
            navigationBar.show();
            navigationBar.setActive('library');
            navigationBar.disableReaderMode(); // Disable orange background
            navigationBar.enterFilterMode(); // Show Tassie + filter icons
        }
        if (navigationOverlay) {
            navigationOverlay.hideHamburger();
        }

        // Destroy previous view
        if (currentView && currentView.destroy) {
            currentView.destroy();
        }

        // Create and render LibraryScreen
        currentView = new LibraryScreen(contentContainer, router, profileData);
        await currentView.render();

        // Re-apply filter after cards are in the DOM (restoreFilter may have fired too early)
        if (navigationBar && navigationBar.filterBar && navigationBar.filterBar.activeFilter) {
            navigationBar.filterBar.applyFilter(navigationBar.filterBar.activeFilter);
        }

        return currentView;
    });

    // Achievement Board
    router.register('achievements', async (profileData) => {
        console.log('📍 Route: Achievements');

        // Show navigation bar
        if (navigationBar) {
            navigationBar.exitFilterMode(); // Exit filter mode when leaving library
            navigationBar.show();
            navigationBar.setActive('achievements');
            navigationBar.disableReaderMode(); // Disable orange background
        }
        if (navigationOverlay) {
            navigationOverlay.hideHamburger();
        }

        // Destroy previous view
        if (currentView && currentView.destroy) {
            currentView.destroy();
        }

        // Create and render AchievementBoard
        currentView = new AchievementBoard(contentContainer, router, profileData);
        await currentView.render();

        return currentView;
    });

    // Settings Screen
    router.register('settings', async (profileData) => {
        console.log('📍 Route: Settings (modal)');
        // Settings is now a modal overlay — toggle it via NavigationBar
        if (navigationBar) {
            navigationBar.toggleSettingsModal();
        }
        return null;
    });

    // Story Reader (uses persistent navigation)
    router.register('story-reader', async (profileData, moduleData) => {
        console.log('📍 Route: Story Reader');

        // Show navigation bar (consistent with other views)
        if (navigationBar) {
            navigationBar.exitFilterMode(); // Exit filter mode when leaving library
            navigationBar.show();
            navigationBar.setActive('library'); // Library is active since we came from there
            navigationBar.enableReaderMode(); // Enable orange background for reader
        }
        if (navigationOverlay) {
            navigationOverlay.hideHamburger();
        }

        // Render story reader (existing function)
        await renderStoryReader(profileData, moduleData);

        // Set currentView with destroy method so silence detection stops on navigation
        currentView = {
            destroy() {
                cleanupStoryReader();
            }
        };

        return currentView;
    });

    // Game Player (with navigation bar, like word search)
    router.register('game-player', async (profileData, moduleData) => {
        console.log('📍 Route: Game Player');

        // Show navigation bar (consistent with word search player)
        if (navigationBar) {
            navigationBar.exitFilterMode(); // Exit filter mode when leaving library
            navigationBar.show();
            navigationBar.setActive('library');
            navigationBar.disableReaderMode();
        }
        if (navigationOverlay) {
            navigationOverlay.hideHamburger();
        }

        // Render game player (existing function)
        await renderGamePlayer(profileData, moduleData);

        return null;
    });

    // Music Player (full-screen mode)
    router.register('music-player', async (profileData, moduleData) => {
        console.log('📍 Route: Music Player');

        // Show navigation bar (standard 3 icons)
        if (navigationBar) {
            navigationBar.exitFilterMode(); // Exit filter mode when leaving library
            navigationBar.show();
            navigationBar.setActive('library');
            navigationBar.disableReaderMode();
        }

        // Destroy previous view
        if (currentView && currentView.destroy) {
            currentView.destroy();
        }

        // Render music player
        const { renderMusicPlayer, cleanupMusicPlayer } = await import('./views/MusicPlayer.js');
        renderMusicPlayer(contentContainer, profileData, moduleData, router);

        // Set currentView with destroy method so audio stops on navigation
        currentView = {
            destroy() {
                cleanupMusicPlayer();
            }
        };

        return currentView;
    });

    // Word Search Player (with navigation bar, like library)
    router.register('word-search-player', async (profileData, moduleData) => {
        console.log('📍 Route: Word Search Player');

        if (navigationBar) {
            navigationBar.exitFilterMode(); // Exit filter mode when leaving library
            navigationBar.show();
            navigationBar.setActive('library');
            navigationBar.disableReaderMode();
        }
        if (navigationOverlay) {
            navigationOverlay.hideHamburger();
        }

        await renderWordSearchPlayer(profileData, moduleData);

        return null;
    });
}


/**
 * Start heartbeat for the active profile.
 * Sends immediately, then on every click/tap in the app (throttled to max once per 30s).
 * @param {string} profileId - The child profile ID
 */
let _heartbeatProfileId = null;
let _heartbeatLastSent = 0;
const HEARTBEAT_THROTTLE_MS = 30000; // Don't send more than once per 30 seconds

export function startHeartbeat(profileId) {
    _heartbeatProfileId = profileId;

    // Send immediately
    _sendHeartbeat();

    // Listen for clicks/taps — fire heartbeat on user interaction
    if (!window._heartbeatListenerAttached) {
        window._heartbeatListenerAttached = true;
        document.addEventListener('click', () => {
            if (_heartbeatProfileId) {
                _sendHeartbeat();
            }
        }, { passive: true });
    }
}

async function _sendHeartbeat() {
    const now = Date.now();
    if (now - _heartbeatLastSent < HEARTBEAT_THROTTLE_MS) return;
    _heartbeatLastSent = now;

    try {
        const deviceId = localStorage.getItem('kassie_device_id');
        const storedFamily = localStorage.getItem('authorizedFamily');
        if (!deviceId || !storedFamily) return;

        const family = JSON.parse(storedFamily);
        const familyId = family.id;
        if (!familyId) return;

        const { api } = await import('./services/api.js');
        await api.post('/api/auth/device/heartbeat', {
            device_id: deviceId,
            family_id: familyId,
            profile_id: _heartbeatProfileId,
        });
    } catch {
        // Offline or error — silently ignore
    }
}
